import React, { useState, useEffect, useCallback } from "react";
import {
    Layout, Table, Input, Button, Form, Space, Row, Col,
    Breadcrumb, Modal, message, Typography, Tooltip, Tag, Descriptions, Dropdown
} from "antd";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
    PlusOutlined, EditOutlined, HomeOutlined, WalletOutlined,
    SearchOutlined, CheckCircleFilled, CloseCircleFilled,
    FilterOutlined, ClearOutlined, EyeOutlined, DeleteOutlined, MoreOutlined
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;

const ManagePaymentAccount = () => {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [viewItem, setViewItem] = useState(null);
    const [addForm] = Form.useForm();
    const [editForm] = Form.useForm();

    // Top filter bar state (server-side search)
    const [search, setSearch] = useState('');
    const [applied, setApplied] = useState({});

    const fetchAccounts = useCallback(async (page = 1, pageSize = 10, filters = {}) => {
        try {
            setLoading(true);
            const res = await api.get(`/settings/paymentaccounts/list.php`, {
                params: {
                    page,
                    limit: pageSize,
                    search: filters.search || '',
                },
            });
            setAccounts(res.data.dataset || []);
            setPagination({
                current: res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page || 10,
                total: res.data.pagination?.total_records || 0,
            });
        } catch {
            message.error("Failed to fetch payment accounts.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAccounts(1, 10, {}); }, [fetchAccounts]);
    const handleTableChange = (pg) => fetchAccounts(pg.current, pg.pageSize, applied);

    const handleFilter = () => {
        const filters = { search: search.trim() };
        setApplied(filters);
        fetchAccounts(1, pagination.pageSize, filters);
    };

    const handleClear = () => {
        setSearch('');
        setApplied({});
        fetchAccounts(1, pagination.pageSize, {});
    };

    const handleAdd = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/settings/paymentaccounts/add.php', {
                account_type: values.account_type || '',
                account_name: values.account_name,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Payment account added successfully.");
                setIsAddModalVisible(false);
                fetchAccounts(pagination.current, pagination.pageSize, applied);
                addForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to add payment account.");
        } finally { setLoading(false); }
    };

    const openEdit = (record) => {
        setCurrentItem(record);
        editForm.setFieldsValue({
            account_type: record.account_type || '',
            account_name: record.account_name,
        });
        setIsEditModalVisible(true);
    };

    const handleEdit = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/settings/paymentaccounts/edit.php', {
                payacc_id: currentItem.payacc_id,
                account_type: values.account_type || '',
                account_name: values.account_name,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Payment account updated successfully.");
                setIsEditModalVisible(false);
                fetchAccounts(pagination.current, pagination.pageSize, applied);
                editForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update payment account.");
        } finally { setLoading(false); }
    };

    const handleToggle = async (record, checked) => {
        try {
            const res = await api.post('/settings/paymentaccounts/toggle.php', {
                payacc_id: record.payacc_id,
                payacc_active: checked ? 1 : 0,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message);
                fetchAccounts(pagination.current, pagination.pageSize, applied);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update status.");
        }
    };

    const handleDelete = async (record) => {
        try {
            setLoading(true);
            const res = await api.post('/settings/paymentaccounts/delete.php', {
                payacc_id: record.payacc_id,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Payment account deleted successfully.");
                fetchAccounts(pagination.current, pagination.pageSize, applied);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to delete payment account.");
        } finally { setLoading(false); }
    };

    const openView = async (record) => {
        setViewItem(record);
        setIsViewModalVisible(true);
        try {
            // Refresh with fresh data from detail.php
            const res = await api.get(`/settings/paymentaccounts/detail.php?payacc_id=${record.payacc_id}`);
            if (res.data.status === "success") setViewItem(res.data);
        } catch {
            // Keep the row data if the detail call fails
        }
    };

    const columns = [
        {
            title: 'ID', dataIndex: 'payacc_id', key: 'payacc_id', width: 80,
        },
        {
            title: 'Account Name', dataIndex: 'account_name', key: 'account_name',
            sorter: (a, b) => (a.account_name || '').localeCompare(b.account_name || ''),
        },
        {
            title: 'Account Type', dataIndex: 'account_type', key: 'account_type',
            render: (val) => val ? <Tag color="blue">{val}</Tag> : <Typography.Text type="secondary">—</Typography.Text>,
        },
        {
            title: 'Status', dataIndex: 'payacc_active', key: 'payacc_active',
            align: 'center', width: 110,
            render: (val) => val == 1
                ? <Tooltip title="Active"><Space size={4}><CheckCircleFilled style={{ color: 'green', fontSize: 16 }} /><Typography.Text style={{ color: 'green' }}>Active</Typography.Text></Space></Tooltip>
                : <Tooltip title="Inactive"><Space size={4}><CloseCircleFilled style={{ color: 'red', fontSize: 16 }} /><Typography.Text style={{ color: 'red' }}>Inactive</Typography.Text></Space></Tooltip>,
        },
        {
            title: 'Action', key: 'action', align: 'center', fixed: 'right', width: 90,
            render: (_, record) => {
                // NOTE: menu item onClick receives the antd MenuInfo object whose
                // domEvent must be stopped from propagating — otherwise the same
                // click that selects the item lands on the just-opened modal mask
                // (clickable by default) and instantly dismisses the modal.
                const items = [
                    {
                        key: "view", label: "View", icon: <EyeOutlined />,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); openView(record); },
                    },
                    {
                        key: "edit", label: "Edit", icon: <EditOutlined />,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); openEdit(record); },
                    },
                    {
                        key: "toggle",
                        label: record.payacc_active == 1 ? "Deactivate" : "Activate",
                        icon: record.payacc_active == 1 ? <CloseCircleFilled /> : <CheckCircleFilled />,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); handleToggle(record, record.payacc_active != 1); },
                    },
                    {
                        key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true,
                        onClick: (info) => {
                            info?.domEvent?.stopPropagation();
                            Modal.confirm({
                                title: "Delete Payment Account",
                                content: "Are you sure you want to delete this account?",
                                okText: "Yes, Delete",
                                okType: "danger",
                                cancelText: "No",
                                onOk: () => handleDelete(record),
                            });
                        },
                    },
                ];
                return (
                    <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
                        <Button type="text" className="fluid-dots-btn" icon={<MoreOutlined />} />
                    </Dropdown>
                );
            },
        },
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Settings' },
                                { title: 'Payment Accounts' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <WalletOutlined /> Payment Accounts
                        </h1>
                        <p className="fluid-page-subtitle">Manage the payment accounts used when creating payments</p>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <div className="hero-actions" style={{ marginTop: 0, justifyContent: 'flex-end' }}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setIsAddModalVisible(true)}
                                className="hero-action-btn"
                            >
                                Add Payment Account
                            </Button>
                        </div>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <div className="fluid-filter-compact" style={{ marginBottom: 12 }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onPressEnter={handleFilter}
                                    placeholder="Account name or type"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={4}>
                                <div className="fluid-filter-compact-actions">
                                    <Button type="primary" icon={<FilterOutlined />} onClick={handleFilter} className="fluid-btn-primary">
                                        Apply
                                    </Button>
                                    <Button icon={<ClearOutlined />} onClick={handleClear} className="fluid-btn-secondary">
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={accounts}
                            rowKey="payacc_id"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={handleTableChange}
                            loading={loading}
                            scroll={{ x: 700 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            {/* Add Modal */}
            <Modal
                title="Add Payment Account"
                open={isAddModalVisible}
                onCancel={() => { setIsAddModalVisible(false); addForm.resetFields(); }}
                footer={null}
                width={520}
                destroyOnClose
            >
                <Form form={addForm} layout="vertical" onFinish={handleAdd}>
                    <Form.Item
                        label="Account Name"
                        name="account_name"
                        rules={[{ required: true, message: 'Account name is required' }, { max: 150, message: 'Max 150 characters' }]}
                    >
                        <Input placeholder="e.g. Main Bank Account" />
                    </Form.Item>
                    <Form.Item
                        label="Account Type"
                        name="account_type"
                        rules={[{ max: 50, message: 'Max 50 characters' }]}
                    >
                        <Input placeholder="e.g. Bank, Cash, Credit Card" />
                    </Form.Item>
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setIsAddModalVisible(false); addForm.resetFields(); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>Add Account</Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>

            {/* Edit Modal */}
            <Modal
                title="Edit Payment Account"
                open={isEditModalVisible}
                onCancel={() => { setIsEditModalVisible(false); editForm.resetFields(); }}
                footer={null}
                width={520}
            >
                <Form form={editForm} onFinish={handleEdit} layout="vertical">
                    <Form.Item
                        label="Account Name"
                        name="account_name"
                        rules={[{ required: true, message: 'Account name is required' }, { max: 150, message: 'Max 150 characters' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Account Type"
                        name="account_type"
                        rules={[{ max: 50, message: 'Max 50 characters' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>Save Changes</Button>
                            <Button danger onClick={() => setIsEditModalVisible(false)}>Cancel</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* View Modal */}
            <Modal
                title={`Payment Account Details${viewItem ? ` - ${viewItem.account_name}` : ""}`}
                open={isViewModalVisible}
                onCancel={() => setIsViewModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsViewModalVisible(false)}>Close</Button>
                ]}
                width={520}
            >
                {viewItem && (
                    <Descriptions column={1} size="small" bordered>
                        <Descriptions.Item label="ID">
                            <Tag color="blue">#{viewItem.payacc_id}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Account Name">
                            <Typography.Text strong>{viewItem.account_name}</Typography.Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Account Type">
                            {viewItem.account_type || <Typography.Text type="secondary">—</Typography.Text>}
                        </Descriptions.Item>
                        <Descriptions.Item label="Status">
                            {viewItem.payacc_active == 1
                                ? <Tag color="green" icon={<CheckCircleFilled />}>Active</Tag>
                                : <Tag color="red" icon={<CloseCircleFilled />}>Inactive</Tag>}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
        </Layout>
    );
};

export default ManagePaymentAccount;
